import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { AdministratorMatchVenueEvent } from "../venueevent/MatchVenueEvent";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";

import {
    FormDateRangePicker,
    FormDropDownList,
    FormAutoComplete,
} from "../../forms/form-components";

import './Report.scss';

export const AdministratorVenueEventsMissingPOSEventId = () => {
    const dispatch = useDispatch();

    const formReference = useRef(null);

    const defaultTicketingWebsite = {
        ticketingWebsiteId: "",
        name: "All Websites",
    };
    const defaultVenueEventStatusType = {
        venueEventStatusTypeId: "",
        name: "All Statuses",
    };
    const defaultEventsSentToPOSStatusOption = {
        id: "false",
        name: "Not Sent to POS",
    };

    const eventsSentToPOSStatusOptions = [
        {
            id: "",
            name: "All",
        },
        {
            id: "true",
            name: "Sent to POS",
        },
        defaultEventsSentToPOSStatusOption
    ];

    const [venueEventsMissingPOSEventId, setVenueEventsMissingPOSEventId] = useState([]);
    const [venueEventsMissingPOSEventIdCount, setVenueEventsMissingPOSEventIdCount] = useState(0);
    const [skipCount, setSkipCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);
    const [areVenueEventsMissingPOSEventIdLoading, setAreVenueEventsMissingPOSEventIdLoading] = useState(true);
    const [reportId, setReportId] = useState(undefined);
    const [showDialog, setShowDialog] = useState(false);
    const [selectedVenueEventKey, setSelectedVenueEventKey] = useState(undefined);
    const [selectedEventName, setSelectedEventName] = useState(undefined);
    const [selectedEventDate, setSelectedEventDate] = useState(undefined);
    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [venueEventStatusTypes, setVenueEventStatusTypes] = useState([]);
    const [searchCriteria, setSearchCriteria] = useState({
        eventsSentToPOSStatusType: defaultEventsSentToPOSStatusOption,
        eventsSentToPOSStatus: false
    });
    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(undefined);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [lastSelectedVenueEventKey, setLastSelectedVenueEventKey] = useState(undefined);

    const autocompleteTimeout = useRef();
    const autocompleteTimeoutDelay = 500; //Milliseconds

    const SelectedCell = (props) => {
        const venueEventKey = props.dataItem.venueEventKey;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, venueEventKey, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const MatchCell = (props) => {
        const venueEventKey = props.dataItem.venueEventKey;
        const eventName = props.dataItem.eventName;
        const eventDate = props.dataItem.eventDate;

        return (
            <td className="text-nowrap">
                <Button type={"button"} icon="button" title="Match" onClick={() => matchEvents(venueEventKey, eventName, eventDate)}>
                    Match
                </Button>
            </td>
        );
    };

    const HideCell = (props) => {
        const venueEventKey = props.dataItem.venueEventKey;

        return (
            <td className="text-nowrap">
                <Button type={"button"} icon="close" title="Hide" onClick={() => hideEvent(venueEventKey)}>
                    Hide
                </Button>
            </td>
        );
    };

    const DeleteCell = (props) => {
        const venueEventKey = props.dataItem.venueEventKey;
        const venueEventStatusTypeName = props.dataItem.venueEventStatusTypeName;

        return (
            <td className="text-nowrap">
                {venueEventStatusTypeName !== "Cancelled" ? "" :
                    <Button type={"button"} icon="trash" title="Delete" onClick={() => deleteEvent(venueEventKey)}>
                        Delete
                    </Button>
                }
            </td>
        );
    };

    const ViewUrlHeaderCell = () => {
        return (
            <div>
                <Button type={"button"} fillMode="link" title="View Selected Websites" onClick={viewSelectedUrls} className="p-0">
                    View Selected
                </Button>
            </div>
        );
    };

    const ViewUrlCell = (props) => {  
        const url = props.dataItem[props.field];     

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const viewAllUrls = () => {
        if (venueEventsMissingPOSEventId.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No websites to view");
            return;
        }

        venueEventsMissingPOSEventId.forEach(dataItem => {
            window.open(dataItem.ticketingUrl, "_blank", "noreferrer");
        });  
    };

    const selectedValueChanged = (event, venueEventKey, selected) => {
        setAreVenueEventsMissingPOSEventIdLoading(true);
               
        setTimeout(() => {
            let venueEventsMissingPOSEventIdCopy = venueEventsMissingPOSEventId;

            const itemIndex = venueEventsMissingPOSEventIdCopy.findIndex(dataItem =>
                dataItem.venueEventKey === venueEventKey
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedVenueEventKey !== undefined
                && lastSelectedVenueEventKey !== venueEventKey) {
                const lastSelectedItemIndex = venueEventsMissingPOSEventIdCopy.findIndex(dataItem =>
                    dataItem.venueEventKey === lastSelectedVenueEventKey
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        venueEventsMissingPOSEventIdCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                venueEventsMissingPOSEventIdCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedVenueEventKey(venueEventKey);
            }

            setVenueEventsMissingPOSEventId(venueEventsMissingPOSEventIdCopy);

            setIsHeaderCheckboxChecked(false);
            setAreVenueEventsMissingPOSEventIdLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setAreVenueEventsMissingPOSEventIdLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let venueEventsMissingPOSEventIdCopy = venueEventsMissingPOSEventId;

            venueEventsMissingPOSEventIdCopy.forEach(dataItem =>
                dataItem.selected = checked
            );

            setVenueEventsMissingPOSEventId(venueEventsMissingPOSEventIdCopy);

            setLastSelectedVenueEventKey(undefined);

            setAreVenueEventsMissingPOSEventIdLoading(false);
        }, 1);
    };

    const getTicketingWebsites = () => {
        const dataItem = {
            onlyActive: true,
        }

        fetch(`/api/general/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                const allTicketingWebsites = [defaultTicketingWebsite].concat(result);
                setTicketingWebsites(allTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventStatusTypes = () => {
        fetch(`/api/general/venueeventstatustypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allVenueEventStatusTypes = [defaultVenueEventStatusType].concat(result);
                setVenueEventStatusTypes(allVenueEventStatusTypes);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }
        
        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);  
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, autocompleteTimeoutDelay);

        setAreVenuesLoading(true);
    };

    const getVenueEventsMissingPOSEventId = (dataItem, newPageNumber) => {
        setAreVenueEventsMissingPOSEventIdLoading(true);

        if (!dataItem) {
            dataItem = searchCriteria;
        }

        if (!dataItem) {
            dataItem = { };
        }

        dataItem.pageNumber = newPageNumber;
    
        fetch(`/api/administrator/venueeventsmissingposeventid`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setReportId(result.reportId);
                setItemsPerPage(result.itemsPerPage);
                setSkipCount(result.itemsPerPage * (result.pageNumber - 1));
                setVenueEventsMissingPOSEventIdCount(result.totalCount);                
                setVenueEventsMissingPOSEventId(result.venueEventsMissingPOSEventId);
                setIsHeaderCheckboxChecked(false);
                setLastSelectedVenueEventKey(undefined);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);                
            })
            .finally(() => {
                setAreVenueEventsMissingPOSEventIdLoading(false);
            });
    }

    const hideEvent = (venueEventKey) => {
        if (window.confirm("Are you sure you want to hide this event from this report?") === false) {
            return;
        }

        setAreVenueEventsMissingPOSEventIdLoading(true);

        const dataItem = {
            venueEventKey: venueEventKey,
            reportId: reportId,
        };
    
        fetch(`/api/general/hidevenueeventsfromreport`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Hid event from report");     
                
                getVenueEventsMissingPOSEventId();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);   
                
                setAreVenueEventsMissingPOSEventIdLoading(false);
            });
    };

    const deleteEvent = (venueEventKey) => {
        if (window.confirm("Are you sure you want to delete this event?") === false) {
            return;
        }

        setAreVenueEventsMissingPOSEventIdLoading(true);

        const dataItem = {
            venueEventKey: venueEventKey
        };

        fetch(`/api/administrator/venueevent`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted event successfully");

                getVenueEventsMissingPOSEventId();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventsMissingPOSEventIdLoading(false);
            });
    };

    //const sendEventsToPOS = () => {
    //    let urls = [];
    //    let venueEventKeys = [];

    //    venueEventsMissingPOSEventId.forEach(dataItem => {
    //        if (dataItem.selected === true) {
    //            urls.push(dataItem.ticketingUrl);
    //            venueEventKeys.push(dataItem.venueEventKey);
    //        }
    //    });

    //    if (urls.length === 0) {
    //        addNotificationWithTimeout(dispatch, "error", "No events selected");
    //        return;
    //    }

    //    if (window.confirm("Are you sure you want to send these events to the POS?") === false) {
    //        return;
    //    }

    //    setAreVenueEventsMissingPOSEventIdLoading(true);

    //    const dataItem = {
    //        urls: urls,
    //        venueEventKeys: venueEventKeys,
    //    };

    //    fetch(`api/administrator/pos/sendeventstopos`,
    //        {
    //            method: "POST",
    //            headers: {
    //                'Content-Type': 'application/json',
    //                "X-XSRF-TOKEN": getCSRFToken(),
    //            },
    //            body: JSON.stringify(dataItem),
    //        })
    //        .then(handleResponse)
    //        .then(result => {
    //            addNotificationWithTimeout(dispatch, "success", "Sent events to POS");

    //            getVenueEventsMissingPOSEventId();
    //        })
    //        .catch(error => {
    //            addNotificationWithTimeout(dispatch, "error", error.message);

    //            setAreVenueEventsMissingPOSEventIdLoading(false);
    //        });
    //};

    const copySelectedWebsitesToClipboard = () => {
        let urls = [];

        venueEventsMissingPOSEventId.forEach(dataItem => {
            if (dataItem.selected === true) {
                let ticketingUrl = dataItem.ticketingUrl;

                if (ticketingUrl.includes("https://app.opendate.io/confirms/") === true) {
                    //Fix Opendate links to use main page instead of purchase page
                    ticketingUrl = ticketingUrl
                        .replace("/confirms/", "/e/")
                        .replace("/web_orders/new", "")
                        .trim();
                }

                urls.push(ticketingUrl);
            }
        });

        if (urls.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");
            return;
        }

        let textToCopy = `${urls.length} events\n\n`;

        textToCopy += urls.join("\n\n");

        copyToClipboard(textToCopy);
    }

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            addNotificationWithTimeout(dispatch, "success", "Copied successfully to clipboard");
        }
        catch (error) {
            addNotificationWithTimeout(dispatch, "error", "Error copying to clipboard: " + error);            
        }
    };

    const markEventsAsSentToPOS = () => {
        let venueEventKeys = [];

        venueEventsMissingPOSEventId.forEach(dataItem => {
            if (dataItem.selected === true) {
                venueEventKeys.push(dataItem.venueEventKey);
            }
        });

        if (venueEventKeys.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");
            return;
        }

        if (window.confirm("Are you sure you want to mark these events as sent to the POS?") === false) {
            return;
        }

        setAreVenueEventsMissingPOSEventIdLoading(true);

        const dataItem = {
            venueEventKeys: venueEventKeys,
        };

        fetch(`api/administrator/pos/markeventsassenttopos`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events marked as sent to POS");

                getVenueEventsMissingPOSEventId();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventsMissingPOSEventIdLoading(false);
            });
    }

    const getData = (dataItem) => {
        if (dataItem.eventsSentToPOSStatusType !== undefined
            && dataItem.eventsSentToPOSStatusType.id !== "") {                
            dataItem.eventsSentToPOSStatus = (dataItem.eventsSentToPOSStatusType.id === "true");
        }
        else {
            dataItem.eventsSentToPOSStatus = undefined;
        }

        if (dataItem.ticketingWebsite !== undefined
            && dataItem.ticketingWebsite.ticketingWebsiteId !== "") {
            dataItem.ticketingWebsiteId = parseInt(dataItem.ticketingWebsite.ticketingWebsiteId);
        }
        else {
            dataItem.ticketingWebsiteId = undefined;
        }

        if (dataItem.venueEventStatusType !== undefined
            && dataItem.venueEventStatusType.venueEventStatusTypeId !== "") {
            dataItem.venueEventStatusTypeId = parseInt(dataItem.venueEventStatusType.venueEventStatusTypeId);
        }
        else {
            dataItem.venueEventStatusTypeId = undefined;
        }

        dataItem.venueKey = undefined;
        if (venues.length > 0) {
            const venue = venues.find(element => element.currentName === venueName);

            if (venue) {
                dataItem.venueKey = venue.venueKey;
            }
        }

        dataItem.startingEventDate = undefined;
        dataItem.endingEventDate = undefined;
        if (dataItem.eventDates) {
            if (dataItem.eventDates.start) {
                dataItem.startingEventDate = dataItem.eventDates.start;
            }

            if (dataItem.eventDates.end) {
                dataItem.endingEventDate = dataItem.eventDates.end;
            }
        }
        
        setSearchCriteria(dataItem);
        getVenueEventsMissingPOSEventId(dataItem);
    }

    const pageChange = (event) => {
        const newPageNumber = (event.page.skip / event.page.take) + 1;
        getVenueEventsMissingPOSEventId(null, newPageNumber);
    };

    const matchEvents = (venueEventKey, eventName, eventDate) => {
        setSelectedVenueEventKey(venueEventKey);
        setSelectedEventName(eventName);
        setSelectedEventDate(eventDate);
        setShowDialog(true);
    };

    const closeDialog = () => {
        setShowDialog(false);
        setSelectedVenueEventKey(undefined);  
        setSelectedEventName(undefined);
        setSelectedEventDate(undefined);
        
        getVenueEventsMissingPOSEventId();
    };

    const viewSelectedUrls = () => {
        if (venueEventsMissingPOSEventId.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No websites to view");
            return;
        }

        let count = 0;

        venueEventsMissingPOSEventId.forEach(dataItem => {
            if (dataItem.selected === true) {
                count++;
                window.open(dataItem.ticketingUrl, "_blank", "noreferrer");
            }
        });

        if (count === 0) {
            addNotificationWithTimeout(dispatch, "error", "No websites to view");
            return;
        }
    };

    useEffect(() => {
        document.title = "Events Missing POS Event Id";

        getTicketingWebsites();
        getVenueEventStatusTypes();
        getVenueEventsMissingPOSEventId();
    }, []);

    return (
        <div className="administratorReport">
            <Form
                onSubmit={getData}
                initialValues={searchCriteria}
                ignoreModified={true}
                ref={formReference}
                render={(formRenderProps) => (
                    <FormElement>
                        <fieldset className={"k-form-fieldset container"}>
                            <div className="row">   
                                <div className="col-md-4">
                                    <Field
                                        id={"eventsSentToPOSStatusType"}
                                        name={"eventsSentToPOSStatusType"}
                                        label={"Events Sent to POS"}
                                        component={FormDropDownList}
                                        data={eventsSentToPOSStatusOptions}
                                        textField="name"
                                        dataItemKey="id"
                                        defaultValue={eventsSentToPOSStatusOptions.find(element => element.id === "false")}
                                    />
                                </div>
                            </div>
                            <div className="row">                                    
                                <div className="col-md-4">
                                    <Field
                                        id={"ticketingWebsite"}
                                        name={"ticketingWebsite"}
                                        label={"Ticketing website"}
                                        component={FormDropDownList}
                                        data={ticketingWebsites}
                                        textField="name"
                                        dataItemKey="ticketingWebsiteId"
                                        defaultValue={ticketingWebsites.find(element => element.ticketingWebsiteId === "")}
                                    /> 
                                </div>
                                <div className="col-md-8">
                                    <Field
                                        id={"venueName"}
                                        name={"venueName"}
                                        label={"Venue"}
                                        component={FormAutoComplete}
                                        data={venues}        
                                        placeholder="e.g. Roseland"
                                        textField="currentName"
                                        onChange={venueChanged}
                                        loading={areVenuesLoading}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"venueEventStatusType"}
                                        name={"venueEventStatusType"}
                                        label={"Event status"}
                                        component={FormDropDownList}
                                        data={venueEventStatusTypes}
                                        textField="name"
                                        dataItemKey="venueEventStatusTypeId"
                                        defaultValue={venueEventStatusTypes.find(element => element.venueEventStatusTypeId === "")}
                                    /> 
                                </div>                                
                                <div className="col-md-6">
                                    <Field
                                        id={"eventDates"}
                                        name={"eventDates"}
                                        component={FormDateRangePicker}
                                        format={"MMM dd, yyyy"}
                                        startDateInputSettings={{
                                            label: "Starting event date"
                                        }}
                                        endDateInputSettings={{
                                            label: "Ending event date"
                                        }}                                        
                                        calendarSettings={{
                                            views: 1
                                        }}
                                    />
                                </div> 
                                <div className="col-md-2">
                                    <div className="k-form-field">
                                        <label className="k-label">&nbsp;</label>
                                        <Button
                                            type={"submit"}
                                            themeColor={"primary"}
                                            icon="search"                                            
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </div>                        
                        </fieldset>
                    </FormElement>
                )}
            />

            {areVenueEventsMissingPOSEventIdLoading ? <LoadingPanel /> : <></>}

            <div className="mb-3 ms-2 d-flex flex-row">
                <div className="p-2 me-2 border d-flex flex-row">
                    <div>
                        <Button type={"button"} icon="hyperlink-open" title="View All Websites" onClick={viewAllUrls}>
                            View All Websites
                        </Button>
                    </div>
                    {/*<div>*/}
                    {/*    <Button type={"button"} icon="email" title="Send Events to POS" onClick={sendEventsToPOS}>*/}
                    {/*        Send Events to POS*/}
                    {/*    </Button>*/}
                    {/*</div>*/}
                </div>
                <div className="p-2 border d-flex flex-row">
                    <div className="me-2">
                        <Button type={"button"} icon="copy" title="Copy Selected Websites to Clipboard" onClick={copySelectedWebsitesToClipboard}>
                            Copy Selected Websites to Clipboard
                        </Button>
                    </div>
                    <div>
                        <Button type={"button"} icon="check" title="Mark as Sent to POS" onClick={markEventsAsSentToPOS}>
                            Mark as Sent to POS
                        </Button>
                    </div>
                </div>
            </div> 

            <Grid
                data={venueEventsMissingPOSEventId}
                resizable={true}
                skip={skipCount}
                take={itemsPerPage}
                total={venueEventsMissingPOSEventIdCount}
                pageable={true}
                onPageChange={pageChange}
                scrollable="none"
            >               
                <Column cell={MatchCell} />
                <Column cell={HideCell} />
                <Column cell={DeleteCell} />
                <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} headerCell={ViewUrlHeaderCell} />   
                <Column field="ticketingWebsiteName" title={"Website"} />
                <Column field="eventName" title={"Event"} />
                <Column field="venueName" title={"Venue"} />
                <Column field="eventDate" title={"Date"} />
                <Column field="venueEventStatusTypeName" title={"Status"} />                             
            </Grid>

            {showDialog === false ? <></> :
                <Dialog
                    title={"Match Events" + (selectedEventName && selectedEventDate ? ` - ${selectedEventName} on ${selectedEventDate}` : "")}
                    onClose={() => setShowDialog(false)}
                    width={"90vw"}
                >
                    <AdministratorMatchVenueEvent
                        venueEventKey={selectedVenueEventKey} 
                        closeDialog={closeDialog} />
                </Dialog>
            }
        </div>
    );
}